import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Card } from '../ui';
import { courseColors, courseIcons } from '../../theme/design-system';

/**
 * CourseForm Component
 * Form for creating or editing a course
 */
export const CourseForm = ({ 
  initialData = null, 
  onSubmit, 
  onCancel,
  isLoading = false 
}) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    color: initialData?.color || '#3B82F6',
    icon: initialData?.icon || '📚'
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 50) {
      newErrors.name = 'Name must be less than 50 characters';
    }
    
    if (formData.description.length > 200) {
      newErrors.description = 'Description must be less than 200 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Course Name */}
      <Input
        label="Kursname *"
        value={formData.name}
        onChange={(value) => handleChange('name', value)}
        placeholder="z.B. Informatik 101"
        error={errors.name}
        fullWidth
        disabled={isLoading}
      />

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Beschreibung
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Kurze Beschreibung des Kurses..."
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          rows="3"
          disabled={isLoading}
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description}</p>
        )}
      </div>

      {/* Color Picker */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Farbe
        </label>
        <div className="grid grid-cols-8 gap-2">
          {courseColors.map(({ name, hex }) => (
            <button
              key={hex}
              type="button"
              className={`w-10 h-10 rounded-lg transition-all ${
                formData.color === hex
                  ? 'ring-2 ring-offset-2 ring-gray-400 scale-110'
                  : 'hover:scale-105'
              }`}
              style={{ backgroundColor: hex }}
              onClick={() => handleChange('color', hex)}
              title={name}
              disabled={isLoading}
            />
          ))}
        </div>
      </div>

      {/* Icon Picker */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Symbol
        </label>
        <div className="grid grid-cols-8 gap-2">
          {courseIcons.map((icon) => (
            <button
              key={icon}
              type="button"
              className={`w-10 h-10 text-2xl rounded-lg transition-all ${
                formData.icon === icon
                  ? 'bg-gray-200 ring-2 ring-gray-400 scale-110'
                  : 'bg-gray-100 hover:bg-gray-200 hover:scale-105'
              }`}
              onClick={() => handleChange('icon', icon)}
              disabled={isLoading}
            >
              {icon}
            </button>
          ))}
        </div>
      </div>

      {/* Preview */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Vorschau
        </label>
        <Card className="flex items-center gap-3" padding="md">
          <div
            className="flex items-center justify-center w-12 h-12 rounded-lg text-2xl"
            style={{ backgroundColor: formData.color }}
          >
            {formData.icon}
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">
              {formData.name || 'Kursname'}
            </h3>
            <p className="text-sm text-gray-600">
              {formData.description || 'Keine Beschreibung'}
            </p>
          </div>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <Button
          type="submit"
          variant="primary"
          fullWidth
          loading={isLoading}
        >
          {initialData ? 'Kurs aktualisieren' : 'Kurs erstellen'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={isLoading}
        >
          Abbrechen
        </Button>
      </div>
    </form>
  );
};

CourseForm.propTypes = {
  initialData: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
};

export default CourseForm;
